import dayjs, {ISO_DATE_FORMATS} from "@/utils/dayjs";
import {ManipulateType} from "dayjs";
import FieldType from "@/utils/constants/FieldType.ts";
import CommonConstants from "@/utils/constants/CommonConstants.ts";
import {parse} from "numerable";
import TimeFieldUtils from "@/utils/common/TimeFieldUtils.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import Formats from "@/utils/UIConstants/Formats.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import GUIActionPlaceholder = namaTypes.GUIActionPlaceholder;
import EditScreenField = namaTypes.EditScreenField;
import CompositeEditScreenField = namaTypes.CompositeEditScreenField;

export default class FieldTypesUtils {
  private static staticYearStart: Date | null | undefined;
  private static staticYearEnd: Date | null | undefined;
  public static fiscalPeriod: object | null | undefined;
  public static currentUser: object | null | undefined;
  public static currentUserEmployee: object | null | undefined;
  public static currentUserRelatedSubsidiary: object | null | undefined;
  private static defaultValues = {
    [CommonConstants.DEFAULT_VALUE_CURRENT_FISCAL_PERIOD]: () => FieldTypesUtils.fiscalPeriod,
    [CommonConstants.DEFAULT_VALUE_CURRENT_USER]: () => FieldTypesUtils.currentUser,
    [CommonConstants.DEFAULT_VALUE_CURRENT_USER_EMPLOYEE]: () => FieldTypesUtils.currentUserEmployee,
    [CommonConstants.DEFAULT_VALUE_CURRENT_USER_RELATED_SUBSIDIARY]: () => FieldTypesUtils.currentUserRelatedSubsidiary,

  };

  static setStaticYearStart(yStart: Date | undefined) {
    if (yStart)
      this.staticYearStart = new Date(yStart);
    else
      this.staticYearStart = null;
  }

  static setStaticYearEnd(yEnd: Date | undefined) {
    if (yEnd)
      this.staticYearEnd = new Date(yEnd);
    else
      this.staticYearEnd = null;
  }

  static getYearStart(date: Date): Date {
    if (this.staticYearStart)
      return this.staticYearStart;
    return new Date(date.getFullYear(), 0, 1);
  }

  static getYearEnd(date: Date): Date {
    if (this.staticYearEnd)
      return this.staticYearEnd;
    return new Date(date.getFullYear(), 11, 31);
  }

  static parseString(value: any, type: string | undefined): any {
    if (!value)
      return value;
    if (!type || type == FieldType.Text || type == FieldType.BigText || type == FieldType.EntityType || type == FieldType.Enum) {
      return value;
    }
    if (type == FieldType.Integer || type == FieldType.Long || type == FieldType.Decimal) {
      return parse(value);
    }
    if (type == FieldType.Boolean) {
      return value === "true";
    }
    if (type === FieldType.Date) {
      return this.deserializeDate(value);
    }
    if (type === FieldType.DateAndTime)
      return this.deserializeDateAndTime(value);
    if (type === FieldType.Time) {
      const dayjsValue = value instanceof Date ? dayjs(value) : dayjs(value, ISO_DATE_FORMATS);
      return dayjsValue.format(Formats.DATE_MOMENT_SERVER_FORMAT);
    }
    if (type == FieldType.Reference || type == FieldType.Genericreference) {
      if (FieldTypesUtils.defaultValues[value]) {
        return FieldTypesUtils.defaultValues[value]();
      }
      let parts: string[] = value.split(CommonConstants.FIELD_PARTS_SEP);
      let id: string = parts.length > 0 ? parts[0] : "";
      let entityType: string = parts.length > 1 ? parts[1] : "";
      let code: string = parts.length > 2 ? parts[2] : "";
      let altCode: string = parts.length > 3 ? parts[3] : "";
      let name1: string = parts.length > 4 ? parts[4] : "";
      let name2: string = parts.length > 5 ? parts[5] : "";
      return {
        "id": id,
        "entityType": entityType,
        "code": code,
        "actualCode": altCode,
        "name1": name1,
        "name2": name2,
      };
    }
    return value;
  }

  static toString(fieldType: string | undefined, value: any): string {
    if (ObjectChecker.isNullOrUndefined(value))
      return "";
    else if (ObjectChecker.isEmptyOrNull(value) && ObjectChecker.isAnyEqualToFirst(fieldType, FieldType.Date, FieldType.DateAndTime, FieldType.Time))
      return "";
    else if (!fieldType)
      return value + '';
    else if (fieldType == FieldType.Date) {
      const dayjsValue = value instanceof Date ? dayjs(value) : dayjs(value, ISO_DATE_FORMATS);
      return dayjsValue.format("DD-MM-YYYY");
    }
    else if (fieldType == FieldType.DateAndTime) {
      const dayjsValue = value instanceof Date ? dayjs(value) : dayjs(value, ISO_DATE_FORMATS);
      return dayjsValue.format("YYYY-MM-DDTHH:mm:ss.SSS");
    }
    else if (fieldType == FieldType.Time)
      return TimeFieldUtils.timeToString(null, value);
    if (fieldType == FieldType.Reference || fieldType == FieldType.Genericreference || fieldType == "ENTITYREFERENCE") {
      let strVal: string = [value.id, value.entityType, value.code, value.actualCode, value.name1, value.name2,]
        .filter(v => ObjectChecker.isNotEmptyOrNull(v))
        .join(CommonConstants.FIELD_PARTS_SEP);
      return strVal;
    }
    return value + '';
  }

  private static deserializeDateAndTime(value: any) {
    if (!value)
      return null;
    if (typeof  value === "string" && value.startsWith("$"))
      return this.deserializeDate(value);
    // Use dayjs(value) directly for Date objects, format string only for strings
    const dayjsValue = value instanceof Date ? dayjs(value) : dayjs(value, ISO_DATE_FORMATS);
    return dayjsValue.format(Formats.DATE_MOMENT_SERVER_FORMAT);
  }

  private static deserializeDate(value: any) {
    if (!value)
      return null;
    const trimmedValue = typeof value === 'string' ? value.trim() : value;
    if (StringUtils.equalsIgnoreCase(CommonConstants.DEFAULT_VALUE_NOW, trimmedValue) || StringUtils.equalsIgnoreCase(CommonConstants.DEFAULT_VALUE_TODAY, trimmedValue))
      value = new Date();
    else if (StringUtils.equalsIgnoreCase(CommonConstants.DEFAULT_VALUE_MONTH_START, trimmedValue))
      value = this.getMonthStart(new Date());
    else if (StringUtils.equalsIgnoreCase(CommonConstants.DEFAULT_VALUE_MONTH_END, trimmedValue))
      value = this.getMonthEnd(new Date());
    else if (StringUtils.equalsIgnoreCase(CommonConstants.DEFAULT_VALUE_NEXT_MONTH_START, trimmedValue))
      value = this.addPeriodToDate(this.getMonthStart(new Date()), 1, 'month');
    else if (StringUtils.equalsIgnoreCase(CommonConstants.DEFAULT_VALUE_NEXT_MONTH_END, trimmedValue))
      value = this.addPeriodToDate(this.getMonthEnd(new Date()), 1, 'month');
    else if (StringUtils.equalsIgnoreCase(CommonConstants.DEFAULT_VALUE_PREVIOUS_MONTH_START, trimmedValue))
      value = this.addPeriodToDate(this.getMonthStart(new Date()), -1, 'month');
    else if (StringUtils.equalsIgnoreCase(CommonConstants.DEFAULT_VALUE_PREVIOUS_MONTH_END, trimmedValue))
      value = this.addPeriodToDate(this.getMonthEnd(new Date()), -1, 'month');
    else if (StringUtils.equalsIgnoreCase(CommonConstants.DEFAULT_VALUE_YEAR_START, trimmedValue))
      value = this.getYearStart(new Date());
    else if (StringUtils.equalsIgnoreCase(CommonConstants.DEFAULT_VALUE_YEAR_END, trimmedValue))
      value = this.getYearEnd(new Date());
    else if (StringUtils.equalsIgnoreCase(CommonConstants.DEFAULT_VALUE_NEXT_YEAR_START, trimmedValue))
      value = this.addPeriodToDate(this.getYearStart(new Date()), 1, 'year');
    else if (StringUtils.equalsIgnoreCase(CommonConstants.DEFAULT_VALUE_NEXT_YEAR_END, trimmedValue))
      value = this.addPeriodToDate(this.getYearEnd(new Date()), 1, 'year');
    else if (StringUtils.equalsIgnoreCase(CommonConstants.DEFAULT_VALUE_PREVIOUS_YEAR_START, trimmedValue))
      value = this.addPeriodToDate(this.getYearStart(new Date()), -1, 'year');
    else if (StringUtils.equalsIgnoreCase(CommonConstants.DEFAULT_VALUE_PREVIOUS_YEAR_END, trimmedValue))
      value = this.addPeriodToDate(this.getYearEnd(new Date()), -1, 'year');
    else if (StringUtils.equalsIgnoreCase(CommonConstants.DEFAULT_VALUE_QUARTER_START, trimmedValue))
      value = this.getQuarterStart(new Date());
    else if (StringUtils.equalsIgnoreCase(CommonConstants.DEFAULT_VALUE_QUARTER_END, trimmedValue))
      value = this.getQuarterEnd(new Date());
    else if (StringUtils.equalsIgnoreCase(CommonConstants.DEFAULT_VALUE_THIRD_START, trimmedValue))
      value = this.getThirdStart(new Date());
    else if (StringUtils.equalsIgnoreCase(CommonConstants.DEFAULT_VALUE_THIRD_END, trimmedValue))
      value = this.getThirdEnd(new Date());
    else if (StringUtils.equalsIgnoreCase(CommonConstants.DEFAULT_VALUE_HALVE_START, trimmedValue))
      value = this.getHalveStart(new Date());
    else if (StringUtils.equalsIgnoreCase(CommonConstants.DEFAULT_VALUE_HALVE_END, trimmedValue))
      value = this.getHalveEnd(new Date());
    else if (typeof trimmedValue === 'string' && trimmedValue.startsWith(CommonConstants.DEFAULT_VALUE_PLUS_DAYS))
      value = this.addPeriodToDate(new Date(), this.extractAddedValue(CommonConstants.DEFAULT_VALUE_PLUS_DAYS, trimmedValue), 'day');
    else if (typeof trimmedValue === 'string' && trimmedValue.startsWith(CommonConstants.DEFAULT_VALUE_PLUS_MONTHS))
      value = this.addPeriodToDate(new Date(), this.extractAddedValue(CommonConstants.DEFAULT_VALUE_PLUS_MONTHS, trimmedValue), 'month');
    else if (typeof trimmedValue === 'string' && trimmedValue.startsWith(CommonConstants.DEFAULT_VALUE_PLUS_YEARS))
      value = this.addPeriodToDate(new Date(), this.extractAddedValue(CommonConstants.DEFAULT_VALUE_PLUS_YEARS, trimmedValue), 'year');
    else if (typeof trimmedValue === 'string' && trimmedValue.startsWith(CommonConstants.DEFAULT_VALUE_PLUS_WEEKS))
      value = this.addPeriodToDate(new Date(), this.extractAddedValue(CommonConstants.DEFAULT_VALUE_PLUS_WEEKS, trimmedValue), 'week');
    else if (typeof trimmedValue === 'string' && trimmedValue.startsWith(CommonConstants.DEFAULT_VALUE_MINUS_DAYS))
      value = this.addPeriodToDate(new Date(), -this.extractAddedValue(CommonConstants.DEFAULT_VALUE_MINUS_DAYS, trimmedValue), 'day');
    else if (typeof trimmedValue === 'string' && trimmedValue.startsWith(CommonConstants.DEFAULT_VALUE_MINUS_MONTHS))
      value = this.addPeriodToDate(new Date(), -this.extractAddedValue(CommonConstants.DEFAULT_VALUE_MINUS_MONTHS, trimmedValue), 'month');
    else if (typeof trimmedValue === 'string' && trimmedValue.startsWith(CommonConstants.DEFAULT_VALUE_MINUS_YEARS))
      value = this.addPeriodToDate(new Date(), -this.extractAddedValue(CommonConstants.DEFAULT_VALUE_MINUS_YEARS, trimmedValue), 'year');
    else if (typeof trimmedValue === 'string' && trimmedValue.startsWith(CommonConstants.DEFAULT_VALUE_MINUS_WEEKS))
      value = this.addPeriodToDate(new Date(), -this.extractAddedValue(CommonConstants.DEFAULT_VALUE_MINUS_WEEKS, trimmedValue), 'week');
    // Use dayjs(value) directly for Date objects, format string only for strings
    const dayjsValue = value instanceof Date ? dayjs(value) : dayjs(value, ISO_DATE_FORMATS);
    return dayjsValue.format(Formats.DATE_MOMENT_SERVER_FORMAT);
  }


  private static extractAddedValue(prefix: string, value: string) {
    return parseFloat(value.substring(prefix.length, value.lastIndexOf(')')));
  }

  private static addPeriodToDate(date: Date, toAdd: number, period: ManipulateType) {
    return dayjs(date).add(toAdd, period).toDate();
  }

  private static getMonthStart(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  private static getMonthEnd(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  }

  private static getQuarterStart(date: Date): Date {
    const quarter = Math.floor(date.getMonth() / 3);
    return new Date(date.getFullYear(), quarter * 3, 1);
  }

  private static getQuarterEnd(date: Date): Date {
    const quarter = Math.floor(date.getMonth() / 3);
    return new Date(date.getFullYear(), quarter * 3 + 3, 0);
  }

  private static getThirdStart(date: Date): Date {
    const third = Math.floor(date.getMonth() / 4);
    return new Date(date.getFullYear(), third * 4, 1);
  }

  private static getThirdEnd(date: Date): Date {
    const third = Math.floor(date.getMonth() / 4);
    return new Date(date.getFullYear(), third * 4 + 4, 0);
  }

  private static getHalveStart(date: Date): Date {
    const halve = Math.floor(date.getMonth() / 6);
    return new Date(date.getFullYear(), halve * 6, 1);
  }

  private static getHalveEnd(date: Date): Date {
    const halve = Math.floor(date.getMonth() / 6);
    return new Date(date.getFullYear(), halve * 6 + 6, 0);
  }

  static fieldIsCompositeEditScreenField(field: Field): field is CompositeEditScreenField {
    return "subFields" in field && !!field.subFields && !!field.subFields?.length;
  }
}

export type Field = CompositeEditScreenField | EditScreenField | GUIActionPlaceholder
